import React from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent } from '../ui/card';
import { Separator } from '../ui/separator';
import { Send, CheckCircle2, Upload, FileText, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AVAILABILITY_OPTIONS = ['Full Time', 'Part Time', 'Evenings', 'Weekends'];

function SectionHeading({ children, hint }) {
  return (
    <div className="mb-4">
      <p className="font-mono text-xs tracking-[0.2em] uppercase text-[hsl(var(--forest))] font-semibold">
        {children}
      </p>
      {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
    </div>
  );
}

function TextField({ label, value, onChange, required, type = 'text', placeholder, testid, className }) {
  const id = testid;
  return (
    <div className={className}>
      <Label htmlFor={id} className="text-sm font-medium">
        {label}{required ? ' *' : ''}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="mt-1.5"
        data-testid={testid}
      />
    </div>
  );
}

function AreaField({ label, value, onChange, required, placeholder, rows = 3, testid }) {
  const id = testid;
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium">
        {label}{required ? ' *' : ''}
      </Label>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        rows={rows}
        className="mt-1.5 resize-none"
        data-testid={testid}
      />
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-sm font-medium">{label} *</Label>
      <RadioGroup
        className="flex gap-6 mt-2"
        aria-label={label}
        value={value === true ? 'yes' : value === false ? 'no' : ''}
        onValueChange={(v) => onChange(v === 'yes')}
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="yes" id={`${testid}-yes`} data-testid={`${testid}-yes`} />
          <Label htmlFor={`${testid}-yes`} className="font-normal cursor-pointer">Yes</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="no" id={`${testid}-no`} data-testid={`${testid}-no`} />
          <Label htmlFor={`${testid}-no`} className="font-normal cursor-pointer">No</Label>
        </div>
      </RadioGroup>
    </div>
  );
}

function EmployerBlock({ index, employer, onChange, required, title, prefix }) {
  const set = (field) => (val) => onChange(index, field, val);
  return (
    <div className="rounded-xl border border-bone-2 p-5 bg-[hsl(var(--bone-2)/0.35)]">
      <p className="font-display font-semibold text-foreground mb-1">{title}</p>
      <p className="text-xs text-muted-foreground mb-4">
        Begin with most recent employment, listing 3 most recent employers.
      </p>
      <div className="space-y-4">
        <TextField label="Name of Employer" value={employer.name} onChange={set('name')} required={required} testid={`${prefix}-name`} />
        <TextField label="Address" value={employer.address} onChange={set('address')} required={required} testid={`${prefix}-address`} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Telephone Number" value={employer.telephone} onChange={set('telephone')} required={required} placeholder="###-###-####" testid={`${prefix}-telephone`} />
          <TextField label="Date Started" type="date" value={employer.date_started} onChange={set('date_started')} required={required} testid={`${prefix}-date-started`} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Starting Title/Position" value={employer.starting_title} onChange={set('starting_title')} required={required} testid={`${prefix}-starting-title`} />
          <TextField label="Starting Salary (annual or hourly)" value={employer.starting_salary} onChange={set('starting_salary')} required={required} testid={`${prefix}-starting-salary`} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Ending (current) Title/Position" value={employer.ending_title} onChange={set('ending_title')} required={required} testid={`${prefix}-ending-title`} />
          <TextField label="Date Left" type="date" value={employer.date_left} onChange={set('date_left')} required={required} testid={`${prefix}-date-left`} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Ending Salary (annual or hourly)" value={employer.ending_salary} onChange={set('ending_salary')} required={required} testid={`${prefix}-ending-salary`} />
          <TextField label="Last Supervisor (name & title)" value={employer.last_supervisor} onChange={set('last_supervisor')} required={required} testid={`${prefix}-supervisor`} />
        </div>
        <AreaField label="Reason for Leaving" value={employer.reason_for_leaving} onChange={set('reason_for_leaving')} required={required} testid={`${prefix}-reason`} />
        <AreaField label="Job Duties & Responsibilities" value={employer.job_duties} onChange={set('job_duties')} required={required} rows={4} testid={`${prefix}-duties`} />
      </div>
    </div>
  );
}

function ReferenceBlock({ index, reference, onChange }) {
  const set = (field) => (val) => onChange(index, field, val);
  const prefix = `careers-ref-${index + 1}`;
  return (
    <div className="rounded-xl border border-bone-2 p-5 bg-[hsl(var(--bone-2)/0.35)]">
      <p className="font-display font-semibold text-foreground mb-4">Reference {index + 1}</p>
      <div className="space-y-4">
        <TextField label="Name" value={reference.name} onChange={set('name')} required testid={`${prefix}-name`} />
        <div>
          <Label className="text-sm font-medium">Personal or Business *</Label>
          <RadioGroup
            className="flex gap-6 mt-2"
            aria-label="Personal or Business"
            value={reference.ref_type}
            onValueChange={set('ref_type')}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="Personal" id={`${prefix}-personal`} data-testid={`${prefix}-personal`} />
              <Label htmlFor={`${prefix}-personal`} className="font-normal cursor-pointer">Personal</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="Business" id={`${prefix}-business`} data-testid={`${prefix}-business`} />
              <Label htmlFor={`${prefix}-business`} className="font-normal cursor-pointer">Business</Label>
            </div>
          </RadioGroup>
        </div>
        <TextField label="Address" value={reference.address} onChange={set('address')} testid={`${prefix}-address`} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Telephone" value={reference.telephone} onChange={set('telephone')} required placeholder="###-###-####" testid={`${prefix}-telephone`} />
          <TextField label="How Known" value={reference.how_known} onChange={set('how_known')} required testid={`${prefix}-how-known`} />
        </div>
      </div>
    </div>
  );
}

function ResumeUpload({ form, onResumeUpload, onClearResume, uploading }) {
  const inputRef = React.useRef(null);
  return (
    <div>
      <Label className="text-sm font-medium">Resume</Label>
      <p className="text-xs text-muted-foreground mb-2">Optional. PDF, DOC, DOCX, TXT, or RTF — up to 10 MB.</p>
      {form.resume_filename ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-bone-2 px-4 py-3 bg-[hsl(var(--bone-2)/0.4)]">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-[hsl(var(--forest))] shrink-0" />
            <span className="text-sm text-foreground truncate" data-testid="careers-resume-filename">{form.resume_filename}</span>
          </div>
          <button type="button" onClick={onClearResume} className="text-muted-foreground hover:text-foreground" data-testid="careers-resume-remove">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt,.rtf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onResumeUpload(file);
              e.target.value = '';
            }}
            data-testid="careers-resume-input"
          />
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="careers-resume-upload-button"
          >
            <Upload className="w-4 h-4" />
            {uploading ? 'Uploading…' : 'Upload Resume'}
          </Button>
        </>
      )}
    </div>
  );
}

function SubmitSpinner() {
  return (
    <span className="flex items-center gap-2">
      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      Submitting...
    </span>
  );
}

function SubmittedView() {
  const navigate = useNavigate();
  return (
    <div className="text-center py-12" data-testid="careers-form-submitted">
      <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
      <h3 className="font-display font-semibold text-2xl text-foreground mb-2">Application Submitted!</h3>
      <p className="text-muted-foreground mb-2">Thank you for your interest in joining Ozark Mountain Construction.</p>
      <p className="text-sm text-muted-foreground mb-6">We&apos;ll review your application and be in touch soon.</p>
      <Button onClick={() => navigate('/')}>Back to Home</Button>
    </div>
  );
}

const CERTIFICATION_PARAGRAPHS = [
  'I certify that the information provided above is true and complete, to the best of my knowledge. I realize that any misrepresentations in the information so provided (whether intentional or un-intentional) may result, if hired, in my immediate dismissal.',
  'I authorize the above company to check all references from current and previous employers and any person listed as a reference on this application that may be relevant to my employment or my ability to perform the job for which I applied. I authorize the company to verify any of the information furnished in this application including, but not limited to, criminal history and driving records. I authorize all persons, schools, companies and law enforcement authorities to release any information concerning my background that may be relevant to evaluation of this application and I hereby release any such persons, schools, companies and law enforcement authorities from any liability for damages whatsoever for issuing this information to this company and/or its agents.',
  'I hereby acknowledge and agree that if employed by company, such employment relationship is at-will; that is, I would be free to resign from the company whenever I wish, and the Company is not restricted from terminating any employee at any time for any or no reason.',
  'I further understand that my employment with the company would not be for any specific fixed period or term. At no time shall any oral statement by the management of the company be construed as giving rise to or creating a contract of employment between the Company and me or any other employee, or otherwise alter or modify the at-will nature of the employment relationship.',
  'I understand that the company prohibits the use and possession of controlled substances and/or alcohol on its premises. As required by the company\u2019s policies, I am willing to submit to drug testing to detect the use of illegal drugs prior to and during employment.',
];

export default function CareersApplicationForm({
  form,
  onChange,
  onEmployerChange,
  onReferenceChange,
  onToggleAvailability,
  onResumeUpload,
  onClearResume,
  resumeUploading,
  onSubmit,
  loading,
  submitted,
}) {
  return (
    <section id="application-form" className="py-16 sm:py-20 bg-background" data-testid="careers-application-form">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-3">Apply Now</p>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-3">
          Employment Application
        </h2>
        <div className="rounded-xl border border-[hsl(var(--forest)/0.3)] bg-[hsl(var(--forest)/0.06)] px-5 py-4 mb-8">
          <p className="text-sm font-semibold text-foreground mb-1">General Instructions to the Applicant completing this form:</p>
          <p className="text-sm text-foreground/80">Complete ALL parts of this application, even if a resume is submitted.</p>
          <p className="text-sm text-foreground/80">Incomplete applications will not be considered.</p>
        </div>

        <Card className="relative overflow-hidden">
          <div className="spec-strip" />
          <CardContent className="p-6 sm:p-8 pt-10">
            {submitted ? (
              <SubmittedView />
            ) : (
              <form onSubmit={onSubmit} className="space-y-8">
                {/* Honeypot */}
                <div className="hidden">
                  <Input value={form.honeypot} onChange={(e) => onChange('honeypot', e.target.value)} tabIndex={-1} autoComplete="off" />
                </div>

                {/* Position + Personal */}
                <div>
                  <SectionHeading>Position &amp; Personal Information</SectionHeading>
                  <div className="space-y-4">
                    <TextField label="Position Applied For" value={form.position_applied_for} onChange={(v) => onChange('position_applied_for', v)} required testid="careers-form-position" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <TextField label="First Name" value={form.first_name} onChange={(v) => onChange('first_name', v)} required testid="careers-form-first-name" />
                      <TextField label="Last Name" value={form.last_name} onChange={(v) => onChange('last_name', v)} required testid="careers-form-last-name" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <TextField label="Home Telephone" value={form.home_telephone} onChange={(v) => onChange('home_telephone', v)} required placeholder="###-###-####" testid="careers-form-home-phone" />
                      <TextField label="Work Telephone" value={form.work_telephone} onChange={(v) => onChange('work_telephone', v)} required placeholder="###-###-####" testid="careers-form-work-phone" />
                    </div>
                    <TextField label="Email Address" type="email" value={form.email} onChange={(v) => onChange('email', v)} required placeholder="your@email.com" testid="careers-form-email" />
                  </div>
                </div>

                <Separator />

                {/* Address */}
                <div>
                  <SectionHeading>Current Address</SectionHeading>
                  <div className="space-y-4">
                    <TextField label="Street Address" value={form.address_street} onChange={(v) => onChange('address_street', v)} required testid="careers-form-street" />
                    <TextField label="Address Line 2" value={form.address_line2} onChange={(v) => onChange('address_line2', v)} testid="careers-form-street2" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <TextField label="City" value={form.address_city} onChange={(v) => onChange('address_city', v)} required testid="careers-form-city" />
                      <TextField label="State / Province / Region" value={form.address_state} onChange={(v) => onChange('address_state', v)} required testid="careers-form-state" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <TextField label="Postal / Zip Code" value={form.address_zip} onChange={(v) => onChange('address_zip', v)} required testid="careers-form-zip" />
                      <TextField label="Country" value={form.address_country} onChange={(v) => onChange('address_country', v)} required testid="careers-form-country" />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Eligibility */}
                <div>
                  <SectionHeading>Eligibility</SectionHeading>
                  <div className="space-y-5">
                    <div>
                      <Label className="text-sm font-medium">Availability to Work (check all that apply) *</Label>
                      <div className="flex flex-wrap gap-x-6 gap-y-3 mt-2">
                        {AVAILABILITY_OPTIONS.map((opt) => {
                          const id = `careers-avail-${opt.replace(/\s+/g, '-').toLowerCase()}`;
                          return (
                            <div key={opt} className="flex items-center gap-2">
                              <Checkbox
                                id={id}
                                checked={form.availability.includes(opt)}
                                onCheckedChange={() => onToggleAvailability(opt)}
                                data-testid={id}
                              />
                              <Label htmlFor={id} className="font-normal cursor-pointer">{opt}</Label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <YesNo label="Are you at least 18 years old?" value={form.at_least_18} onChange={(v) => onChange('at_least_18', v)} testid="careers-form-age" />
                    <YesNo label="Are you prohibited from lawfully becoming employed in this Country because of Visa or Immigration Status?" value={form.immigration_prohibited} onChange={(v) => onChange('immigration_prohibited', v)} testid="careers-form-immigration" />
                    <YesNo label="Have you ever been convicted of a felony?" value={form.convicted_felony} onChange={(v) => onChange('convicted_felony', v)} testid="careers-form-felony" />
                    {form.convicted_felony === true ? (
                      <AreaField label="If yes, explain" value={form.felony_explanation} onChange={(v) => onChange('felony_explanation', v)} testid="careers-form-felony-explain" />
                    ) : null}
                  </div>
                </div>

                <Separator />

                {/* Employment History */}
                <div>
                  <SectionHeading hint="Begin with most recent employment, listing 3 most recent employers.">Employment History</SectionHeading>
                  <div className="space-y-5">
                    <EmployerBlock index={0} employer={form.employers[0]} onChange={onEmployerChange} required title="Most Recent Employer" prefix="careers-emp1" />
                    <EmployerBlock index={1} employer={form.employers[1]} onChange={onEmployerChange} required={false} title="Employer 2" prefix="careers-emp2" />
                    <EmployerBlock index={2} employer={form.employers[2]} onChange={onEmployerChange} required={false} title="Employer 3" prefix="careers-emp3" />
                  </div>
                </div>

                <Separator />

                {/* Skills */}
                <div>
                  <SectionHeading>Skills, Certifications &amp; Aptitudes</SectionHeading>
                  <AreaField label="Skills / Certifications / Aptitudes" value={form.skills} onChange={(v) => onChange('skills', v)} required rows={4} testid="careers-form-skills" />
                </div>

                <Separator />

                {/* Education */}
                <div>
                  <SectionHeading>Education</SectionHeading>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <TextField label="High School Attended" value={form.high_school} onChange={(v) => onChange('high_school', v)} required testid="careers-form-highschool" />
                      <YesNo label="Graduate?" value={form.high_school_graduate} onChange={(v) => onChange('high_school_graduate', v)} testid="careers-form-highschool-grad" />
                      <TextField label="Course of Study" value={form.high_school_course} onChange={(v) => onChange('high_school_course', v)} testid="careers-form-highschool-course" />
                    </div>

                    <div className="rounded-xl border border-bone-2 p-5 space-y-4">
                      <p className="font-display font-semibold text-foreground">College (Undergraduate)</p>
                      <TextField label="College Attended" value={form.college1} onChange={(v) => onChange('college1', v)} testid="careers-form-college1" />
                      <YesNo label="Graduate?" value={form.college1_graduate} onChange={(v) => onChange('college1_graduate', v)} testid="careers-form-college1-grad" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TextField label="Course of Study" value={form.college1_course} onChange={(v) => onChange('college1_course', v)} testid="careers-form-college1-course" />
                        <TextField label="Degree Earned" value={form.college1_degree} onChange={(v) => onChange('college1_degree', v)} testid="careers-form-college1-degree" />
                      </div>
                    </div>

                    <div className="rounded-xl border border-bone-2 p-5 space-y-4">
                      <p className="font-display font-semibold text-foreground">College (Undergraduate)</p>
                      <TextField label="College Attended" value={form.college2} onChange={(v) => onChange('college2', v)} testid="careers-form-college2" />
                      <YesNo label="Graduate?" value={form.college2_graduate} onChange={(v) => onChange('college2_graduate', v)} testid="careers-form-college2-grad" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TextField label="Course of Study" value={form.college2_course} onChange={(v) => onChange('college2_course', v)} testid="careers-form-college2-course" />
                        <TextField label="Degree Earned" value={form.college2_degree} onChange={(v) => onChange('college2_degree', v)} testid="careers-form-college2-degree" />
                      </div>
                    </div>

                    <div className="rounded-xl border border-bone-2 p-5 space-y-4">
                      <p className="font-display font-semibold text-foreground">College (Graduate / Professional)</p>
                      <TextField label="School Attended" value={form.grad_school} onChange={(v) => onChange('grad_school', v)} testid="careers-form-grad" />
                      <YesNo label="Graduate?" value={form.grad_school_graduate} onChange={(v) => onChange('grad_school_graduate', v)} testid="careers-form-grad-grad" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TextField label="Course of Study" value={form.grad_school_course} onChange={(v) => onChange('grad_school_course', v)} testid="careers-form-grad-course" />
                        <TextField label="Degree Earned" value={form.grad_school_degree} onChange={(v) => onChange('grad_school_degree', v)} testid="careers-form-grad-degree" />
                      </div>
                    </div>

                    <AreaField label="Additional Education / Training" value={form.additional_education} onChange={(v) => onChange('additional_education', v)} testid="careers-form-additional-education" />
                    <AreaField label="Other Interests" value={form.other_interests} onChange={(v) => onChange('other_interests', v)} testid="careers-form-interests" />
                  </div>
                </div>

                <Separator />

                {/* References */}
                <div>
                  <SectionHeading hint="Provide three (3) references not related to you; indicate if personal or professional/business.">References</SectionHeading>
                  <div className="space-y-5">
                    {form.references.map((reference, index) => (
                      <ReferenceBlock key={`ref-block-${index}`} index={index} reference={reference} onChange={onReferenceChange} />
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Resume */}
                <div>
                  <SectionHeading>Resume</SectionHeading>
                  <ResumeUpload form={form} onResumeUpload={onResumeUpload} onClearResume={onClearResume} uploading={resumeUploading} />
                </div>

                <Separator />

                {/* Certification */}
                <div>
                  <SectionHeading>Certification</SectionHeading>
                  <div className="space-y-3 text-xs text-foreground/75 leading-relaxed">
                    {CERTIFICATION_PARAGRAPHS.map((para) => (
                      <p key={para.slice(0, 40)}>{para}</p>
                    ))}
                  </div>
                  <div className="mt-5">
                    <TextField
                      label="Print name to confirm certification"
                      value={form.certification_name}
                      onChange={(v) => onChange('certification_name', v)}
                      required
                      placeholder="Type your full legal name"
                      testid="careers-form-certification-name"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full btn-lift gap-2 py-6" disabled={loading} data-testid="careers-application-submit-button">
                  {loading ? <SubmitSpinner /> : (<><Send className="w-4 h-4" />Submit Application</>)}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
